import type { JSX } from "preact";
import { Button } from "@gss/components/Button";
import { Col } from "@gss/components/Col";
import { Download } from "@gss/components/icons";
import { useMessages } from "@gss/components/Messages";
import { Row } from "@gss/components/Row";
import { useConst } from "@gss/hooks/useConst";
import type { MeasurementStat } from "@gss/types/measurementStat";
import type { SecurityContext } from "@gss/types/SecurityContext";
import type { LanguageTranslation } from "../../translations/translation.types";
import { DAO } from "../DAO";
import type { AggregateOn } from "../export/export.types";
import { useTranslation } from "../util/Translation";

interface Props {
    stat: MeasurementStat;
    audience: SecurityContext;
    filterParams: string[];
}

interface Option {
    type: string;
    aggregateOn: AggregateOn;
}

export function DownloadAggregated({
    stat,
    audience,
    filterParams,
}: Props): JSX.Element {
    const { translation, translateFilter } = useTranslation();
    const t = translation.download;
    const options = useConst(() => getOptions(stat, t, translateFilter));
    const { addError } = useMessages();

    const downloadAggregate = (aggregateOn: AggregateOn) => {
        DAO.export
            .downloadAggregate(
                audience,
                stat.measurement,
                stat.periods,
                aggregateOn,
                filterParams,
            )
            .catch(addError);
    };

    const title = t.downloadAggregated;

    return (
        <section aria-label={title}>
            <h2>{title}</h2>
            <Row>
                {options.map((node) => (
                    <Col
                        key={node.aggregateOn}
                        xs="auto"
                        className="text-center download-aggregate-item"
                    >
                        <Button
                            variant={null}
                            className="fs-4"
                            onClick={() => downloadAggregate(node.aggregateOn)}
                        >
                            <Download aria-hidden className="me-1" />
                            {translateFilter(node.type)}
                        </Button>
                    </Col>
                ))}
            </Row>
        </section>
    );
}

function getOptions(
    stat: MeasurementStat,
    t: LanguageTranslation["download"],
    translateFilter: (filter: string) => string,
): Option[] {
    const result: Option[] = [];

    const publicOrgStructure = stat.orgStructures.find(
        (o) => o.value === "public",
    );

    const dnStructure = publicOrgStructure?.dnStructure;

    if (dnStructure != null) {
        // Only country
        if (dnStructure.length === 1) {
            result.push({
                type: dnStructure[0].title,
                aggregateOn: "public.0",
            });
        }
        // More than country: skip country level
        else {
            for (let i = 1; i < dnStructure.length; ++i) {
                result.push({
                    type: dnStructure[i].title,
                    aggregateOn: `public.${i}`,
                });
            }
        }
    }

    // Internal org structure doesn't exist on public measurements
    if (stat.orgStructures.some((o) => o.value === "internal")) {
        // If the public org structure is missing, we still want to show the region level
        if (result.length === 0) {
            result.push({
                type: translateFilter("Region"),
                aggregateOn: "internal.region",
            });
        }
        result.push({
            type: t.lowestLevel,
            aggregateOn: "internal.lowest",
        });
    }

    return result;
}
